import PropTypes from 'prop-types';
import React from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import ReactModal from 'react-modal';
import VM from 'openblock-vm';
import {injectIntl, intlShape} from 'react-intl';

import ErrorBoundaryHOC from '../lib/error-boundary-hoc.jsx';
import {
    getIsError,
    getIsShowingProject
} from '../reducers/project-state';
import {showAlertWithTimeout} from '../reducers/alerts';
import {
    activateTab,
    BLOCKS_TAB_INDEX,
    PYTHON_TAB_INDEX,
    COSTUMES_TAB_INDEX,
    SOUNDS_TAB_INDEX,
    LIBRARIES_TAB_INDEX,
    BOARDS_TAB_INDEX
} from '../reducers/editor-tab';

import {
    closeCostumeLibrary,
    closeBackdropLibrary,
    closeDeviceLibrary,
    closeExtensionLibrary,
    closeTelemetryModal,
    closeFeedbackModal,
    closeTierModal,
    openDeviceLibrary,
    openExtensionLibrary,
    openTierModal
} from '../reducers/modals';

import FontLoaderHOC from '../lib/font-loader-hoc.jsx';
import LocalizationHOC from '../lib/localization-hoc.jsx';
import SBFileUploaderHOC from '../lib/sb-file-uploader-hoc.jsx';
import ProjectFetcherHOC from '../lib/project-fetcher-hoc.jsx';
import TitledHOC from '../lib/titled-hoc.jsx';
import ProjectSaverHOC from '../lib/project-saver-hoc.jsx';
import QueryParserHOC from '../lib/query-parser-hoc.jsx';
import storage from '../lib/storage';
import vmListenerHOC from '../lib/vm-listener-hoc.jsx';
import vmManagerHOC from '../lib/vm-manager-hoc.jsx';
import cloudManagerHOC from '../lib/cloud-manager-hoc.jsx';

import GUIComponent from '../components/gui/gui.jsx';
import {setIsScratchDesktop} from '../lib/isScratchDesktop.js';

class GUI extends React.Component {
    constructor (props) {
        super(props);
        this.isInitialTierLoaded = false;
    }
    componentDidMount () {
        setIsScratchDesktop(this.props.isScratchDesktop);
        this.props.onStorageInit(storage);
        this.props.onVmInit(this.props.vm);
        
        // Fetch current usage from database FIRST, then decide on tier modal
        this.props.onFetchUsage((fetchedTier) => {
            // Sync storage with actual tier on first load without alerting
            localStorage.setItem('lastKnownTier', fetchedTier);
            this.isInitialTierLoaded = true;

            // Decide on tier modal for free users
            if (fetchedTier === 'free') {
                const hasSeenLimits = sessionStorage.getItem('hasSeenTierLimits');
                if (!hasSeenLimits) {
                    this.props.onShowTierLimits();
                    sessionStorage.setItem('hasSeenTierLimits', 'true');
                }
            }
        });

        // Start polling for tier changes every 30 seconds
        this.tierPollInterval = setInterval(() => {
            this.props.onFetchUsage();
        }, 30000);

        // Check for agent update once window starts
        this.startupAgentCheckTimeout = setTimeout(() => {
            this.checkAgentUpdateAtStartup();
        }, 2500);
    }
    componentDidUpdate (prevProps) {
        if (this.props.projectId !== prevProps.projectId && this.props.projectId !== null) {
            this.props.onUpdateProjectId(this.props.projectId);
        }
        if (this.props.isShowingProject && !prevProps.isShowingProject) {
            // this only notifies container when a project changes from not yet loaded to loaded
            // At this time the project view in www doesn't need to know when a project is unloaded
            this.props.onProjectLoaded();
        }
        if (this.props.isRealtimeMode !== true && prevProps.isRealtimeMode === true) {
            this.props.onActivateBlocksTab();
        }

        // Tier change notification — only show for changes AFTER initial load
        if (this.isInitialTierLoaded && this.props.userTier) {
            const lastKnownTier = localStorage.getItem('lastKnownTier');
            if (lastKnownTier && lastKnownTier !== this.props.userTier) {
                if (this.props.userTier === 'pro') {
                    this.props.onShowTierUpgrade();
                } else if (this.props.userTier === 'free') {
                    this.props.onShowTierDowngrade();
                }
                localStorage.setItem('lastKnownTier', this.props.userTier);
            }
        }
    }
    componentWillUnmount () {
        if (this.tierPollInterval) {
            clearInterval(this.tierPollInterval);
        }
        if (this.startupAgentCheckTimeout) {
            clearTimeout(this.startupAgentCheckTimeout);
        }
    }

    checkAgentUpdateAtStartup () {
        const dontRemindAll = localStorage.getItem('dontRemindAgentUpdate');
        if (dontRemindAll === 'true') {
            return;
        }

        // In Desktop Electron environment, the main process OpenblockDesktopUpdater handles checking
        if (typeof window !== 'undefined' && window.require) {
            try {
                const {ipcRenderer} = window.require('electron');
                if (ipcRenderer) {
                    ipcRenderer.send('requestCheckAgentUpdateStartup');
                    return;
                }
            } catch (e) {
                // Ignore electron require failure in web mode
            }
        }

        // Web mode: Check local agent via GET http://127.0.0.1:20111/version
        fetch('http://127.0.0.1:20111/version')
            .then(r => r.json())
            .then(localAgent => {
                if (!localAgent || !localAgent.version) {
                    return;
                }

                fetch('https://app.tifstudio.in/agent-updates/latest-agent.yml', {
                    headers: {'Cache-Control': 'no-cache'}
                })
                    .then(res => res.text())
                    .then(text => {
                        const versionMatch = text.match(/version:\s*([^\r\n]+)/);
                        if (!versionMatch) return;
                        const remoteVersion = versionMatch[1].trim();

                        const dontRemindVer = localStorage.getItem('dontRemindAgentVersion');
                        if (dontRemindVer && dontRemindVer === remoteVersion) {
                            return;
                        }

                        // Compare remote version with local agent version
                        const compareParts = (v1, v2) => {
                            const p1 = (v1 || '').replace(/[^0-9.]/g, '').split('.').map(Number);
                            const p2 = (v2 || '').replace(/[^0-9.]/g, '').split('.').map(Number);
                            for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
                                const num1 = p1[i] || 0;
                                const num2 = p2[i] || 0;
                                if (num1 > num2) return 1;
                                if (num1 < num2) return -1;
                            }
                            return 0;
                        };

                        if (compareParts(remoteVersion, localAgent.version) > 0) {
                            if (this.props.onSetUpdate) {
                                const updateMsg = `Latest version of agent is available to update (v${remoteVersion}).`;
                                this.props.onSetUpdate({
                                    phase: 'resourceUpdateAvailable',
                                    info: {
                                        version: remoteVersion,
                                        isStartup: true,
                                        message: {
                                            en: {
                                                'What is new': [updateMsg]
                                            }
                                        }
                                    }
                                });
                            }
                        }
                    })
                    .catch(() => {});
            })
            .catch(() => {
                // Local agent is not available/running
            });
    }
    render () {
        if (this.props.isError) {
            throw new Error(
                `Error in Scratch GUI [location=${window.location}]: ${this.props.error}`);
        }
        const {
            /* eslint-disable no-unused-vars */
            assetHost,
            cloudHost,
            error,
            isError,
            isScratchDesktop,
            isShowingProject,
            onActivateBlocksTab,
            onProjectLoaded,
            onStorageInit,
            onUpdateProjectId,
            onVmInit,
            projectHost,
            projectId,
            /* eslint-enable no-unused-vars */
            children,
            fetchingProject,
            isLoading,
            loadingStateVisible,
            ...componentProps
        } = this.props;
        return (
            <GUIComponent
                loading={fetchingProject || isLoading || loadingStateVisible}
                {...componentProps}
            >
                {children}
            </GUIComponent>
        );
    }
}
GUI.propTypes = {
    assetHost: PropTypes.string,
    children: PropTypes.node,
    cloudHost: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    fetchingProject: PropTypes.bool,
    intl: intlShape,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    isScratchDesktop: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    loadingStateVisible: PropTypes.bool,
    onActivateBlocksTab: PropTypes.func,
    onActivatePythonTab: PropTypes.func,
    onProjectLoaded: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onStorageInit: PropTypes.func,
    onUpdateProjectId: PropTypes.func,
    onVmInit: PropTypes.func,
    projectHost: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    telemetryModalVisible: PropTypes.bool,
    tierModalVisible: PropTypes.bool,
    isBlocksMinimized: PropTypes.bool,
    onSetBlocksMinimized: PropTypes.func,
    userTier: PropTypes.string,
    onSetUpdate: PropTypes.func,
    onShowTierUpgrade: PropTypes.func,
    onShowTierDowngrade: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired,
    isRealtimeMode: PropTypes.bool
};

GUI.defaultProps = {
    isScratchDesktop: false,
    onStorageInit: storageInstance => storageInstance.addOfficialScratchWebStores(),
    onProjectLoaded: () => {},
    onUpdateProjectId: () => {},
    onVmInit: (/* vm */) => {}
};

import {setBlocksMinimized} from '../reducers/mode';
const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        activeTabIndex: state.scratchGui.editorTab.activeTabIndex,
        alertsVisible: state.scratchGui.alerts.visible,
        backdropLibraryVisible: state.scratchGui.modals.backdropLibrary,
        blocksTabVisible: state.scratchGui.editorTab.activeTabIndex === BLOCKS_TAB_INDEX,
        cardsVisible: state.scratchGui.cards.visible,
        connectionModalVisible: state.scratchGui.modals.connectionModal,
        uploadProgressVisible: state.scratchGui.modals.uploadProgress,
        updateModalVisible: state.scratchGui.modals.updateModal,
        feedbackModalVisible: state.scratchGui.modals.feedbackModal,
        tierModalVisible: state.scratchGui.modals.tierModal,
        costumeLibraryVisible: state.scratchGui.modals.costumeLibrary,
        costumesTabVisible: state.scratchGui.editorTab.activeTabIndex === COSTUMES_TAB_INDEX,
        error: state.scratchGui.projectState.error,
        isError: getIsError(loadingState),
        isFullScreen: state.scratchGui.mode.isFullScreen,
        isPlayerOnly: state.scratchGui.mode.isPlayerOnly,
        isBlocksMinimized: state.scratchGui.mode.isBlocksMinimized,
        isRtl: state.locales.isRtl,
        isShowingProject: getIsShowingProject(loadingState),
        loadingStateVisible: state.scratchGui.modals.loadingProject,
        projectId: state.scratchGui.projectState.projectId,
        soundsTabVisible: state.scratchGui.editorTab.activeTabIndex === SOUNDS_TAB_INDEX,
        pythonTabVisible: state.scratchGui.editorTab.activeTabIndex === PYTHON_TAB_INDEX,
        librariesTabVisible: state.scratchGui.editorTab.activeTabIndex === LIBRARIES_TAB_INDEX,
        boardsTabVisible: state.scratchGui.editorTab.activeTabIndex === BOARDS_TAB_INDEX,
        targetIsStage: (
            state.scratchGui.targets.stage &&
            state.scratchGui.targets.stage.id === state.scratchGui.targets.editingTarget
        ),
        telemetryModalVisible: state.scratchGui.modals.telemetryModal,
        tipsLibraryVisible: state.scratchGui.modals.tipsLibrary,
        vm: state.scratchGui.vm,
        isRealtimeMode: state.scratchGui.programMode.isRealtimeMode,
        userTier: state.scratchGui.tier.tier
    };
};

const mapDispatchToProps = dispatch => ({
    onOpenDeviceLibrary: () => dispatch(openDeviceLibrary()),
    onCloseDeviceLibrary: () => dispatch(closeDeviceLibrary()),
    onOpenExtensionLibrary: () => dispatch(openExtensionLibrary()),
    onCloseExtensionLibrary: () => dispatch(closeExtensionLibrary()),
    onExtensionButtonClick: () => dispatch(openExtensionLibrary()),
    onActivateTab: tab => dispatch(activateTab(tab)),
    onActivateBlocksTab: () => dispatch(activateTab(BLOCKS_TAB_INDEX)),
    onActivatePythonTab: () => dispatch(activateTab(PYTHON_TAB_INDEX)),
    onActivateCostumesTab: () => dispatch(activateTab(COSTUMES_TAB_INDEX)),
    onActivateSoundsTab: () => dispatch(activateTab(SOUNDS_TAB_INDEX)),
    onActivateLibrariesTab: () => dispatch(activateTab(LIBRARIES_TAB_INDEX)),
    onActivateBoardsTab: () => dispatch(activateTab(BOARDS_TAB_INDEX)),
    onRequestCloseBackdropLibrary: () => dispatch(closeBackdropLibrary()),
    onRequestCloseCostumeLibrary: () => dispatch(closeCostumeLibrary()),
    onRequestCloseTelemetryModal: () => dispatch(closeTelemetryModal()),
    onRequestCloseFeedbackModal: () => dispatch(closeFeedbackModal()),
    onRequestCloseTierModal: () => dispatch(closeTierModal()),
    onSetBlocksMinimized: (isMinimized) => dispatch(setBlocksMinimized(isMinimized)),
    onShowTierLimits: () => dispatch(openTierModal()),
    onFetchUsage: (callback) => {
        const { fetchUsage } = require('../lib/usage-api');
        const { setUsage } = require('../reducers/tier');
        fetchUsage().then(usage => {
            if (usage) {
                dispatch(setUsage(usage));
                
                // Sync the local storage user object's tier
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    try {
                        const userObj = JSON.parse(userStr);
                        if (userObj && userObj.tier !== usage.tier) {
                            userObj.tier = usage.tier;
                            localStorage.setItem('user', JSON.stringify(userObj));
                        }
                    } catch (e) {
                        console.error('Error syncing user tier to localStorage:', e);
                    }
                }
            }
            if (callback) {
                callback(usage ? usage.tier : 'free');
            }
        });
    },
    onShowTierUpgrade: () => showAlertWithTimeout(dispatch, 'tierUpgradeSuccess'),
    onShowTierDowngrade: () => showAlertWithTimeout(dispatch, 'tierDowngrade'),
    onSetUpdate: message => {
        const {setUpdate} = require('../reducers/update');
        const {openUpdateModal} = require('../reducers/modals');
        dispatch(setUpdate(message));
        dispatch(openUpdateModal());
    }
});

const ConnectedGUI = injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps,
)(GUI));

// note that redux's 'compose' function is just being used as a general utility to make
// the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
// ability to compose reducers.
const WrappedGui = compose(
    LocalizationHOC,
    ErrorBoundaryHOC('Top Level App'),
    FontLoaderHOC,
    QueryParserHOC,
    ProjectFetcherHOC,
    TitledHOC,
    ProjectSaverHOC,
    vmListenerHOC,
    vmManagerHOC,
    SBFileUploaderHOC,
    cloudManagerHOC
)(ConnectedGUI);

WrappedGui.setAppElement = ReactModal.setAppElement;
export default WrappedGui;
